import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { opportunityService } from '../services/opportunityService';
import { colors } from '../styles/colors';

interface OpportunityEditModalProps {
  opportunityId: number;
  onClose: () => void;
  onSave: () => void;
  mode?: 'modal' | 'page';
  isIntegration?: boolean;
}

interface OpportunityData {
  id: number;
  name: string;
  fancy_name: string;
  description: string;
  prerequisite: string;
  desirable: string;
  perks: string;
  other_info: string;
  level: string;
  regime: number;
  quantity: number;
  salary_from: number;
  salary_to: number;
  show_salary: boolean;
  type_id: number;
  category_id: number;
  workplace: number;
  apply_method: number;
  apply_link: string;
  apply_email: string;
  apply_email_subject: string;
  require_cv: boolean;
  require_portfolio: boolean;
  highlight: boolean;
  confidential: boolean;
  published: boolean;
  status: number;
  filter_state: boolean;
  filter_salary: boolean;
  deny_duplicated_candidates: boolean;
  hide_ads: boolean;
  published_at: string;
  expires_at: string;
  comments: string;
  moderated: boolean;
  search_hidden: boolean;
  reply_email: boolean;
  reply_email_subject: string;
  reply_email_body: string;
  use_custom_template: boolean;
  custom_template: string;
  pro: boolean;
  poderoso_expires_at: string;
  jooble_paid: boolean;
  facebook_conversion_code_candidates: string;
  hunting_partner_id?: number;
  recruiter_id?: number;
  recruiter_name?: string;
  recruiter_email?: string;
  company_id: number;
  company_name?: string;
  company_address_id?: number;
  tags?: string;
  url?: string;
}

interface SelectOption {
  id: number;
  name: string;
}

interface Recruiter {
  id: number;
  recruiter_id: number;
  name: string;
  email: string;
}

interface OpportunityAddress {
  id?: number;
  street: string;
  number: string;
  complement: string;
  zip: string;
  neighborhood: string;
  city: string;
  state: string;
  country: string;
  telephone: string;
}

interface OpportunityRecruiter {
  id?: number;
  recruiter_id: number;
  poderoso: boolean;
  _destroy?: boolean;
}

interface OpportunityAnswer {
  id?: number;
  body: string;
  correct: boolean;
  _destroy?: boolean;
}

interface OpportunityQuestion {
  id?: number;
  body: string;
  required: boolean;
  question_type: string;
  filter: boolean;
  opportunity_answers: OpportunityAnswer[];
  _destroy?: boolean;
}

type SaveAction = 'save' | 'save-and-activate';

const OpportunityEditModal: React.FC<OpportunityEditModalProps> = ({ 
  opportunityId, 
  onClose, 
  onSave,
  mode = 'modal',
  isIntegration = false,
}) => {
  const [opportunity, setOpportunity] = useState<OpportunityData | null>(null);
  const [types, setTypes] = useState<SelectOption[]>([]);
  const [categories, setCategories] = useState<SelectOption[]>([]);
  const [recruiters, setRecruiters] = useState<Recruiter[]>([]);
  const [address, setAddress] = useState<OpportunityAddress | null>(null);
  const [companyAddresses, setCompanyAddresses] = useState<any[]>([]);
  const [selectedCompanyAddressId, setSelectedCompanyAddressId] = useState<number | null>(null);
  const [questions, setQuestions] = useState<OpportunityQuestion[]>([]);
  const [opportunityRecruiters, setOpportunityRecruiters] = useState<OpportunityRecruiter[]>([]);
  const [newRecruiter, setNewRecruiter] = useState<Partial<OpportunityRecruiter>>({});
  const [loading, setLoading] = useState(true);
  const [savingAction, setSavingAction] = useState<SaveAction | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchData();
  }, [opportunityId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const oppResponse = await api.get(`/opportunities/${opportunityId}`);
      const oppData = oppResponse.data;
      
      const [typesResponse, categoriesResponse, companyRecruitersResponse, companyResponse] = await Promise.all([
        api.get('/opportunities/types'),
        api.get('/opportunities/categories'),
        api.get(`/companies/${oppData.company_id}/recruiters`),
        api.get(`/companies/${oppData.company_id}`)
      ]);
      
      setOpportunity(oppData);
      setTypes(typesResponse.data);
      setCategories(categoriesResponse.data);
      setRecruiters(companyRecruitersResponse.data.recruiters || []);
      setCompanyAddresses(companyResponse.data.addresses || []);
      
      // Se for "Na empresa" e tiver company_address_id, definir como selecionado
      if (oppData.workplace === 2 && oppData.company_address_id) {
        setSelectedCompanyAddressId(oppData.company_address_id);
      }
      
      // Carregar endereço se existir
      if (oppData.workplace === 3 || oppData.workplace === 4) { // Outro lugar ou Híbrido
        try {
          const addressResponse = await api.get(`/opportunities/${opportunityId}/address`);
          const addressData = addressResponse.data;
          
          // Aplicar máscaras nos valores carregados
          if (addressData.zip) {
            addressData.zip = addressData.zip.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2');
          }
          if (addressData.telephone) {
            addressData.telephone = addressData.telephone.replace(/\D/g, '')
              .replace(/(\d{2})(\d)/, '($1) $2')
              .replace(/(\d{4,5})(\d{4})/, '$1-$2');
          }
          
          setAddress(addressData);
        } catch (error) {
          setAddress({
            street: '', number: '', complement: '', zip: '', 
            neighborhood: '', city: '', state: '', country: 'BR', telephone: ''
          });
        }
      }
      
      // Carregar questões
      try {
        const questionsResponse = await api.get(`/opportunities/${opportunityId}/questions`);
        setQuestions(questionsResponse.data || []);
      } catch (error) {
        setQuestions([]);
      }
      
      // Carregar recrutadores da oportunidade
      try {
        const recruitersResponse = await api.get(`/opportunities/${opportunityId}/recruiters`);
        setOpportunityRecruiters(recruitersResponse.data || []);
      } catch (error) {
        setOpportunityRecruiters([]);
      }
      
    } catch (error: any) {
      toast.error('Erro ao carregar dados da oportunidade');
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    if (!opportunity) return null;

    let cleanAddress = null;
    if (address) {
      cleanAddress = {
        ...address,
        zip: address.zip.replace(/\D/g, ''),
        telephone: address.telephone.replace(/\D/g, '')
      };
    }

    return {
      ...opportunity,
      company_address_id: selectedCompanyAddressId,
      opportunity_address: cleanAddress,
      opportunity_questions: questions.filter(q => !q._destroy),
      opportunity_recruiters: opportunityRecruiters
    };
  };

  const validateOpportunity = () => {
    if (!opportunity) return false;

    const errors: Record<string, string> = {};
    const requiredText = 'Campo obrigatório';
    const hasAnyDescription = [
      opportunity.description,
      opportunity.prerequisite,
      opportunity.desirable,
      opportunity.perks,
      opportunity.other_info
    ].some(value => String(value || '').trim());

    if (!String(opportunity.name || '').trim()) errors.name = requiredText;
    if (!opportunity.type_id) errors.type_id = requiredText;
    if (!opportunity.category_id) errors.category_id = requiredText;
    if (!opportunity.regime) errors.regime = requiredText;
    if (!opportunity.level) errors.level = requiredText;
    if (!opportunity.quantity || Number(opportunity.quantity) <= 0) errors.quantity = 'Informe uma quantidade maior que zero';
    if (!opportunity.salary_from && opportunity.salary_from !== 0) errors.salary_from = requiredText;
    if (!opportunity.salary_to && opportunity.salary_to !== 0) errors.salary_to = requiredText;
    if (!opportunity.workplace) errors.workplace = requiredText;
    if (opportunity.workplace === 2 && companyAddresses.length > 0 && !selectedCompanyAddressId) {
      errors.company_address_id = 'Selecione um endereço da empresa';
    }
    if ((opportunity.workplace === 3 || opportunity.workplace === 4)) {
      if (!address?.city?.trim()) errors.address_city = requiredText;
      if (!address?.state?.trim()) errors.address_state = requiredText;
    }
    if (!opportunity.apply_method) errors.apply_method = requiredText;
    if (!hasAnyDescription) {
      errors.description = 'Preencha ao menos um campo de descrição, requisitos, desejável, benefícios ou outras informações';
    }

    if (opportunity.apply_method === 1 && !String(opportunity.apply_link || '').trim()) {
      errors.apply_link = requiredText;
    }

    if (opportunity.apply_method === 2) {
      if (!String(opportunity.apply_email || '').trim()) errors.apply_email = requiredText;
      if (!String(opportunity.apply_email_subject || '').trim()) errors.apply_email_subject = requiredText;
    }

    if (opportunity.reply_email) {
      if (!String(opportunity.reply_email_subject || '').trim()) errors.reply_email_subject = requiredText;
      if (!String(opportunity.reply_email_body || '').trim()) errors.reply_email_body = requiredText;
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error('Preencha os campos obrigatórios destacados em vermelho.');
      return false;
    }

    return true;
  };

  const saveOpportunity = async ({
    closeAfterSave = true,
    action = 'save' as SaveAction,
    clearActionAfterSave = true
  } = {}) => {
    if (!opportunity || !validateOpportunity()) return false;

    try {
      setSavingAction(action);
      const payload = buildPayload();
      await api.put(`/opportunities/${opportunityId}`, payload);
      toast.success('Oportunidade atualizada com sucesso!');

      if (closeAfterSave) {
        onSave();
        onClose();
      }

      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar oportunidade');
      return false;
    } finally {
      if (clearActionAfterSave) {
        setSavingAction(null);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveOpportunity({ closeAfterSave: true });
  };

  const handleChange = (field: keyof OpportunityData, value: any) => {
    if (!opportunity) return;
    setOpportunity(prev => prev ? { ...prev, [field]: value } : null);
    setValidationErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const getFieldStyle = (field: string, baseStyle: React.CSSProperties) => ({
    ...baseStyle,
    ...(validationErrors[field] ? styles.inputError : {})
  });

  const renderFieldError = (field: string) => validationErrors[field] ? (
    <div style={styles.fieldError}>{validationErrors[field]}</div>
  ) : null;

  const handleAddressChange = (field: keyof OpportunityAddress, value: string) => {
    let maskedValue = value;
    
    // Aplicar máscara de CEP
    if (field === 'zip') {
      maskedValue = value.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2').substring(0, 9);
    }
    
    // Aplicar máscara de telefone
    if (field === 'telephone') {
      maskedValue = value.replace(/\D/g, '')
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4,5})(\d{4})/, '$1-$2')
        .substring(0, 15);
    }
    
    setAddress(prev => prev ? { ...prev, [field]: maskedValue } : null);
    const validationFieldByAddressField: Partial<Record<keyof OpportunityAddress, string>> = {
      city: 'address_city',
      state: 'address_state',
    };
    const validationField = validationFieldByAddressField[field];
    if (validationField) {
      setValidationErrors(prev => {
        if (!prev[validationField]) return prev;
        const next = { ...prev };
        delete next[validationField];
        return next;
      });
    }
  };

  const addQuestion = () => {
    setQuestions(prev => [...prev, { 
      body: '', 
      required: true, 
      question_type: 'text', 
      filter: false,
      opportunity_answers: []
    }]);
  };

  const updateQuestion = (index: number, field: keyof OpportunityQuestion, value: any) => {
    setQuestions(prev => prev.map((q, i) => i === index ? { ...q, [field]: value } : q));
  };

  const addAnswer = (questionIndex: number) => {
    setQuestions(prev => prev.map((q, i) => 
      i === questionIndex 
        ? { ...q, opportunity_answers: [...q.opportunity_answers, { body: '', correct: false }] }
        : q
    ));
  };

  const updateAnswer = (questionIndex: number, answerIndex: number, field: keyof OpportunityAnswer, value: any) => {
    setQuestions(prev => prev.map((q, i) => 
      i === questionIndex 
        ? {
            ...q, 
            opportunity_answers: q.opportunity_answers.map((a, j) => 
              j === answerIndex ? { ...a, [field]: value } : a
            )
          }
        : q
    ));
  };

  const removeAnswer = (questionIndex: number, answerIndex: number) => {
    setQuestions(prev => prev.map((q, i) => 
      i === questionIndex 
        ? {
            ...q, 
            opportunity_answers: q.opportunity_answers.filter((_, j) => j !== answerIndex)
          }
        : q
    ));
  };

  const removeQuestion = (index: number) => {
    setQuestions(prev => prev.map((q, i) => 
      i === index ? { ...q, _destroy: true } : q
    ).filter((q, i) => i !== index || q.id));
  };

  const addOpportunityRecruiter = () => {
    if (newRecruiter.recruiter_id) {
      console.log('Adicionando recrutador:', newRecruiter);
      const selectedRecruiter = recruiters.find(r => r.recruiter_id === newRecruiter.recruiter_id);
      console.log('Recrutador selecionado:', selectedRecruiter);
      
      setOpportunityRecruiters(prev => [...prev, {
        recruiter_id: newRecruiter.recruiter_id!,
        poderoso: newRecruiter.poderoso || false
      }]);
      setNewRecruiter({});
    }
  };

  const removeOpportunityRecruiter = (index: number) => {
    setOpportunityRecruiters(prev => prev.filter((_, i) => i !== index));
  };

  const updateNewRecruiter = (field: keyof OpportunityRecruiter, value: any) => {
    setNewRecruiter(prev => ({ ...prev, [field]: value }));
  };

  const clearNewRecruiter = () => {
    setNewRecruiter({});
  };

  const getRecruiterInfo = () => {
    if (!opportunity?.recruiter_id) return 'Nenhum recrutador associado';
    const recruiter = recruiters.find(r => r.id === opportunity.recruiter_id);
    if (recruiter) {
      return `${recruiter.name} (${recruiter.email})`;
    }
    // Se não encontrou nos recruiters carregados, buscar nos dados da oportunidade
    return opportunity.recruiter_name && opportunity.recruiter_email 
      ? `${opportunity.recruiter_name} (${opportunity.recruiter_email})`
      : 'Recrutador não encontrado';
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().slice(0, 16);
  };

  const getTypeName = () => types.find(type => type.id === opportunity?.type_id)?.name || '-';

  const getCategoryName = () => categories.find(category => category.id === opportunity?.category_id)?.name || '-';

  const getRegimeLabel = (regime?: number) => {
    const regimes: { [key: number]: string } = {
      1: 'CLT',
      2: 'PJ',
      3: 'Indiferente',
      4: 'Estágio',
    };
    return regime ? regimes[regime] || '-' : '-';
  };

  const getWorkplaceLabel = (workplace?: number) => {
    const workplaces: { [key: number]: string } = {
      1: 'Home Office',
      2: 'Na empresa',
      3: 'Outro lugar',
      4: 'Híbrido',
    };
    return workplace ? workplaces[workplace] || '-' : '-';
  };

  const isModeratingIntegrationOpportunity = () => {
    if (!opportunity || !isIntegration) return false;
    return !opportunity.moderated && !(opportunity.status === 2 && opportunity.published);
  };

  const getStatusLabel = () => {
    if (!opportunity) return '-';
    if (opportunity.status === 2 && opportunity.published) return 'Ativa e publicada';
    if (opportunity.status === 2 && !opportunity.published) return 'Ativa e despublicada';
    if (opportunity.status === 3) return 'Desativada';
    if (opportunity.status === 1) return 'Travada';
    return 'Rascunho';
  };

  const renderPreviewSection = (title: string, value?: string | number | null) => (
    value ? (
      <div style={styles.previewSection}>
        <h4 style={styles.previewSectionTitle}>{title}</h4>
        <div style={styles.previewText}>{value}</div>
      </div>
    ) : null
  );

  const renderPreviewMeta = (label: string, value?: string | number | null) => (
    <div style={styles.previewMetaItem}>
      <strong>{label}</strong>
      <span>{value || '-'}</span>
    </div>
  );

  const renderPreview = () => {
    if (!opportunity) return null;

    const displayName = opportunity.fancy_name || opportunity.name || 'Sem título';
    const salary = opportunity.show_salary
      ? [opportunity.salary_from, opportunity.salary_to].filter(Boolean).map(value => `R$ ${Number(value).toLocaleString('pt-BR')}`).join(' até ')
      : 'Não exibido';

    return (
      <div style={styles.previewCard}>
        <div style={styles.previewHeader}>
          <span style={styles.previewEyebrow}>Preview da vaga</span>
          <h3 style={styles.previewTitle}>{displayName}</h3>
          <div style={styles.previewBadges}>
            <span style={styles.previewBadge}>{getStatusLabel()}</span>
            {opportunity.moderated ? (
              <span style={{...styles.previewBadge, backgroundColor: colors.success}}>Moderada</span>
            ) : (
              <span style={{...styles.previewBadge, backgroundColor: '#F59E0B'}}>Em moderação</span>
            )}
          </div>
        </div>

        <div style={styles.previewMetaGrid}>
          {renderPreviewMeta('Empresa', opportunity.company_name || `#${opportunity.company_id}`)}
          {renderPreviewMeta('Tipo', getTypeName())}
          {renderPreviewMeta('Categoria', getCategoryName())}
          {renderPreviewMeta('Nível', opportunity.level)}
          {renderPreviewMeta('Regime', getRegimeLabel(opportunity.regime))}
          {renderPreviewMeta('Local', getWorkplaceLabel(opportunity.workplace))}
          {renderPreviewMeta('Quantidade', opportunity.quantity)}
          {renderPreviewMeta('Salário', salary)}
        </div>

        {renderPreviewSection('Descrição', opportunity.description)}
        {renderPreviewSection('Requisitos', opportunity.prerequisite)}
        {renderPreviewSection('Desejável', opportunity.desirable)}
        {renderPreviewSection('Benefícios', opportunity.perks)}
        {renderPreviewSection('Outras informações', opportunity.other_info)}

        {address && (
          <div style={styles.previewSection}>
            <h4 style={styles.previewSectionTitle}>Endereço</h4>
            <div style={styles.previewText}>
              {[address.street, address.number, address.neighborhood, address.city, address.state].filter(Boolean).join(', ') || '-'}
            </div>
          </div>
        )}

        <div style={styles.previewFooter}>
          <strong>Recrutador</strong>
          <span>{getRecruiterInfo()}</span>
          {opportunityUrl && (
            <a href={opportunityUrl} target="_blank" rel="noopener noreferrer" style={styles.previewLink}>
              Abrir vaga publicada
            </a>
          )}
        </div>
      </div>
    );
  };

  const handleSaveAndActivateIntegration = async () => {
    const saved = await saveOpportunity({
      closeAfterSave: false,
      action: 'save-and-activate',
      clearActionAfterSave: false
    });
    if (!saved) return;

    try {
      setSavingAction('save-and-activate');
      await opportunityService.activateIntegration(opportunityId);
      toast.success('Vaga de integração salva e ativada com sucesso!');
      onSave();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao ativar vaga de integração');
    } finally {
      setSavingAction(null);
    }
  };

  if (loading) {
    const loadingContent = (
      <div style={mode === 'page' ? styles.pageLoading : styles.overlay}>
        <div style={mode === 'page' ? styles.pageLoadingCard : styles.modal}>
          <div style={styles.loading}>Carregando...</div>
        </div>
      </div>
    );

    return loadingContent;
  }

  if (!opportunity) return null;

  const opportunityUrl = opportunity.url || '';
  const isSaving = savingAction !== null;

  return (
    <div style={mode === 'page' ? styles.pageContainer : styles.overlay}>
      <div style={mode === 'page' ? styles.pageShell : styles.modal}>
        <div style={styles.header}>
          <div>
            <button type="button" onClick={onClose} style={styles.backBtn}>
              ← Voltar
            </button>
            <h2 style={styles.title}>Editar Oportunidade #{opportunity.id}</h2>
          </div>
          {mode === 'modal' && <button onClick={onClose} style={styles.closeBtn}>×</button>}
        </div>
        
        <form onSubmit={handleSubmit} style={styles.content} noValidate>
          <div style={styles.editorLayout}>
            <div style={styles.formColumn}>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Informações Básicas</h3>
            <div style={styles.urlRow}>
              <label style={styles.label}>URL da Vaga</label>
              <div style={styles.urlContainer}>
                <a
                  href={opportunityUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.urlLink}
                >
                  {opportunityUrl}
                </a>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(opportunityUrl);
                    toast.success('URL copiada para o clipboard!');
                  }}
                  style={styles.copyBtn}
                >
                  Copiar
                </button>
              </div>
            </div>
            <div style={styles.urlRow}>
              <label style={styles.label}>Recrutador</label>
              <div style={styles.textDisplay}>
                {getRecruiterInfo()}
              </div>
            </div>
            <div style={styles.grid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Nome Fantasia</label>
                <input
                  type="text"
                  value={opportunity.fancy_name || ''}
                  onChange={(e) => handleChange('fancy_name', e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Nome da Vaga *</label>
                <input
                  type="text"
                  value={opportunity.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  style={getFieldStyle('name', styles.input)}
                  placeholder="Ex: diretor de arte pleno, analista de social media júnior"
                />
                {renderFieldError('name')}
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Tags</label>
                <input
                  type="text"
                  value={opportunity.tags || ''}
                  onChange={(e) => handleChange('tags', e.target.value)}
                  style={styles.input}
                  placeholder="Separadas por vírgula"
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Tipo *</label>
                <select
                  value={opportunity.type_id}
                  onChange={(e) => handleChange('type_id', parseInt(e.target.value))}
                  style={getFieldStyle('type_id', styles.select)}
                >
                  <option value="">Selecione um tipo</option>
                  {types.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
                {renderFieldError('type_id')}
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Categoria *</label>
                <select
                  value={opportunity.category_id}
                  onChange={(e) => handleChange('category_id', parseInt(e.target.value))}
                  style={getFieldStyle('category_id', styles.select)}
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
                {renderFieldError('category_id')}
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Descrição</h3>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Descrição da Vaga</label>
              <textarea
                value={opportunity.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                style={getFieldStyle('description', styles.textarea)}
                rows={5}
              />
              {renderFieldError('description')}
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Requisitos</label>
              <textarea
                value={opportunity.prerequisite || ''}
                onChange={(e) => handleChange('prerequisite', e.target.value)}
                style={styles.textarea}
                rows={4}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Desejável</label>
              <textarea
                value={opportunity.desirable || ''}
                onChange={(e) => handleChange('desirable', e.target.value)}
                style={styles.textarea}
                rows={4}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Benefícios</label>
              <textarea
                value={opportunity.perks || ''}
                onChange={(e) => handleChange('perks', e.target.value)}
                style={styles.textarea}
                rows={4}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Outras informações sobre a oportunidade</label>
              <textarea
                value={opportunity.other_info || ''}
                onChange={(e) => handleChange('other_info', e.target.value)}
                style={styles.textarea}
                rows={3}
              />
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Detalhes da Posição</h3>
            <div style={styles.grid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Nível</label>
                <select
                  value={opportunity.level || ''}
                  onChange={(e) => handleChange('level', e.target.value)}
                  style={getFieldStyle('level', styles.select)}
                >
                  <option value="">Selecione o nível</option>
                  <option value="junior">Júnior</option>
                  <option value="mid-level">Pleno</option>
                  <option value="senior">Senior</option>
                </select>
                {renderFieldError('level')}
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Regime</label>
                <select
                  value={opportunity.regime}
                  onChange={(e) => handleChange('regime', parseInt(e.target.value))}
                  style={getFieldStyle('regime', styles.select)}
                >
                  <option value="">Selecione o regime</option>
                  <option value={1}>CLT</option>
                  <option value={2}>PJ</option>
                  <option value={3}>Indiferente</option>
                  <option value={4}>Estágio</option>
                </select>
                {renderFieldError('regime')}
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Local de trabalho</label>
                <select
                  value={opportunity.workplace}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    handleChange('workplace', value);
                    if (value === 1) { // Home Office
                      setAddress(null);
                      setSelectedCompanyAddressId(null);
                      handleChange('company_address_id', null);
                    } else if (value === 2) { // Na empresa
                      setAddress(null);
                      setSelectedCompanyAddressId(null);
                      handleChange('company_address_id', null);
                    } else if ((value === 3 || value === 4) && !address) { // Outro lugar ou Híbrido
                      setSelectedCompanyAddressId(null);
                      handleChange('company_address_id', null);
                      setAddress({
                        street: '', number: '', complement: '', zip: '', 
                        neighborhood: '', city: '', state: '', country: 'BR', telephone: ''
                      });
                    }
                  }}
                  style={getFieldStyle('workplace', styles.select)}
                >
                  <option value="">Selecione o local</option>
                  <option value={1}>Home Office</option>
                  <option value={2}>Na empresa</option>
                  <option value={3}>Outro lugar</option>
                  <option value={4}>Híbrido</option>
                </select>
                {renderFieldError('workplace')}
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Quantidade</label>
                <input
                  type="number"
                  value={opportunity.quantity || ''}
                  onChange={(e) => handleChange('quantity', parseInt(e.target.value) || 0)}
                  style={getFieldStyle('quantity', styles.input)}
                  min="1"
                />
                {renderFieldError('quantity')}
              </div>
            </div>
          </div>

          {/* Seleção de endereço da empresa quando workplace = "Na empresa" */}
          {opportunity.workplace === 2 && companyAddresses.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Endereço da Empresa</h3>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Selecione o endereço da empresa *</label>
                <select
                  value={selectedCompanyAddressId || ''}
                  onChange={(e) => {
                    const addressId = parseInt(e.target.value) || null;
                    setSelectedCompanyAddressId(addressId);
                    handleChange('company_address_id', addressId);
                  }}
                  style={getFieldStyle('company_address_id', styles.select)}
                >
                  <option value="">Selecione um endereço</option>
                  {companyAddresses.map(addr => (
                    <option key={addr.id} value={addr.id}>
                      {addr.street} {addr.number}, {addr.neighborhood} - {addr.city}/{addr.state}
                    </option>
                  ))}
                </select>
                {renderFieldError('company_address_id')}
              </div>
            </div>
          )}

          {(opportunity.workplace === 3 || opportunity.workplace === 4) && address && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Endereço da vaga (Caso o local de trabalho seja "Outro lugar" ou "Híbrido")</h3>
              <div style={styles.grid}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Logradouro</label>
                  <input
                    type="text"
                    value={address.street}
                    onChange={(e) => handleAddressChange('street', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Número</label>
                  <input
                    type="text"
                    value={address.number}
                    onChange={(e) => handleAddressChange('number', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Complemento</label>
                  <input
                    type="text"
                    value={address.complement}
                    onChange={(e) => handleAddressChange('complement', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>CEP</label>
                  <input
                    type="text"
                    value={address.zip}
                    onChange={(e) => handleAddressChange('zip', e.target.value)}
                    style={styles.input}
                    placeholder="00000-000"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Bairro</label>
                  <input
                    type="text"
                    value={address.neighborhood}
                    onChange={(e) => handleAddressChange('neighborhood', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Cidade *</label>
                  <input
                    type="text"
                    value={address.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    style={getFieldStyle('address_city', styles.input)}
                  />
                  {renderFieldError('address_city')}
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Estado *</label>
                  <select
                    value={address.state}
                    onChange={(e) => handleAddressChange('state', e.target.value)}
                    style={getFieldStyle('address_state', styles.select)}
                  >
                    <option value="">Selecione</option>
                    <option value="AC">Acre</option>
                    <option value="AL">Alagoas</option>
                    <option value="AP">Amapá</option>
                    <option value="AM">Amazonas</option>
                    <option value="BA">Bahia</option>
                    <option value="CE">Ceará</option>
                    <option value="DF">Distrito Federal</option>
                    <option value="ES">Espírito Santo</option>
                    <option value="GO">Goiás</option>
                    <option value="MA">Maranhão</option>
                    <option value="MT">Mato Grosso</option>
                    <option value="MS">Mato Grosso do Sul</option>
                    <option value="MG">Minas Gerais</option>
                    <option value="PA">Pará</option>
                    <option value="PB">Paraíba</option>
                    <option value="PR">Paraná</option>
                    <option value="PE">Pernambuco</option>
                    <option value="PI">Piauí</option>
                    <option value="RJ">Rio de Janeiro</option>
                    <option value="RN">Rio Grande do Norte</option>
                    <option value="RS">Rio Grande do Sul</option>
                    <option value="RO">Rondônia</option>
                    <option value="RR">Roraima</option>
                    <option value="SC">Santa Catarina</option>
                    <option value="SP">São Paulo</option>
                    <option value="SE">Sergipe</option>
                    <option value="TO">Tocantins</option>
                  </select>
                  {renderFieldError('address_state')}
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>País</label>
                  <input
                    type="text"
                    value={address.country}
                    onChange={(e) => handleAddressChange('country', e.target.value)}
                    style={styles.input}
                    placeholder="BR"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Telefone</label>
                  <input
                    type="text"
                    value={address.telephone}
                    onChange={(e) => handleAddressChange('telephone', e.target.value)}
                    style={styles.input}
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
            </div>
          )}

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Salário</h3>
            <div style={styles.checkboxGrid}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.show_salary}
                  onChange={(e) => handleChange('show_salary', e.target.checked)}
                  style={styles.checkbox}
                />
                Mostra salário?
              </label>
            </div>
            <div style={styles.grid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Faixa salarial de *</label>
                <input
                  type="number"
                  value={opportunity.salary_from || ''}
                  onChange={(e) => handleChange('salary_from', parseFloat(e.target.value) || 0)}
                  style={getFieldStyle('salary_from', styles.input)}
                  step="1"
                  placeholder="0"
                />
                {renderFieldError('salary_from')}
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>até</label>
                <input
                  type="number"
                  value={opportunity.salary_to || ''}
                  onChange={(e) => handleChange('salary_to', parseFloat(e.target.value) || 0)}
                  style={getFieldStyle('salary_to', styles.input)}
                  step="1"
                  placeholder="0"
                />
                {renderFieldError('salary_to')}
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Você receberá currículos por *</h3>
            <div style={styles.grid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Método de Candidatura</label>
                <select
                  value={opportunity.apply_method}
                  onChange={(e) => handleChange('apply_method', parseInt(e.target.value))}
                  style={getFieldStyle('apply_method', styles.select)}
                >
                  <option value="">Selecione o método</option>
                  <option value={1}>Link Externo</option>
                  <option value={2}>Email</option>
                  <option value={3}>Caesar</option>
                </select>
                {renderFieldError('apply_method')}
              </div>
              {opportunity.apply_method === 1 && (
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Apply link</label>
                  <input
                    type="url"
                    value={opportunity.apply_link || ''}
                    onChange={(e) => handleChange('apply_link', e.target.value)}
                    style={getFieldStyle('apply_link', styles.input)}
                    placeholder="https://exemplo.com/candidatar"
                  />
                  {renderFieldError('apply_link')}
                </div>
              )}
              {opportunity.apply_method === 2 && (
                <>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Apply email</label>
                    <input
                      type="email"
                      value={opportunity.apply_email || ''}
                      onChange={(e) => handleChange('apply_email', e.target.value)}
                      style={getFieldStyle('apply_email', styles.input)}
                      placeholder="recrutador@empresa.com"
                    />
                    {renderFieldError('apply_email')}
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Apply email subject</label>
                    <input
                      type="text"
                      value={opportunity.apply_email_subject || ''}
                      onChange={(e) => handleChange('apply_email_subject', e.target.value)}
                      style={getFieldStyle('apply_email_subject', styles.input)}
                      placeholder="Candidatura - [Nome da Vaga]"
                    />
                    {renderFieldError('apply_email_subject')}
                  </div>
                </>
              )}
            </div>
            <div style={styles.checkboxGrid}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.require_cv}
                  onChange={(e) => handleChange('require_cv', e.target.checked)}
                  style={styles.checkbox}
                />
                CV obrigatório
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.require_portfolio}
                  onChange={(e) => handleChange('require_portfolio', e.target.checked)}
                  style={styles.checkbox}
                />
                Portfólio obrigatório
              </label>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Comentários</label>
              <textarea
                value={opportunity.comments || ''}
                onChange={(e) => handleChange('comments', e.target.value)}
                style={styles.textarea}
                rows={3}
                placeholder="O candidato precisa enviar alguma informação além das que você já solicitou? Especifique aqui!"
              />
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Configurações</h3>
            <div style={styles.checkboxGrid}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.highlight}
                  onChange={(e) => handleChange('highlight', e.target.checked)}
                  style={styles.checkbox}
                />
                Destaque?
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.confidential}
                  onChange={(e) => handleChange('confidential', e.target.checked)}
                  style={styles.checkbox}
                />
                Confidencial?
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.hide_ads}
                  onChange={(e) => handleChange('hide_ads', e.target.checked)}
                  style={styles.checkbox}
                />
                Esconder Publicidade?
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.search_hidden}
                  onChange={(e) => handleChange('search_hidden', e.target.checked)}
                  style={styles.checkbox}
                />
                Esconder a vaga nos resultados da busca
              </label>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Filtros</h3>
            <div style={styles.checkboxGrid}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.filter_state}
                  onChange={(e) => handleChange('filter_state', e.target.checked)}
                  style={styles.checkbox}
                />
                FILTRO - Não receber applies fora do estado
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.filter_salary}
                  onChange={(e) => handleChange('filter_salary', e.target.checked)}
                  style={styles.checkbox}
                />
                FILTRO - Não receber applies fora do salário?
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.deny_duplicated_candidates}
                  onChange={(e) => handleChange('deny_duplicated_candidates', e.target.checked)}
                  style={styles.checkbox}
                />
                FILTRO - Não receber pessoas que já se candidataram à outra vaga da empresa num período de 1 ano
              </label>
            </div>
          </div>



          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Recrutadores da Vaga</h3>
            <p style={styles.sectionDescription}>
              Adicione ou remova recrutadores para esta vaga. O recrutador que postou a vaga não pode ser removido.
            </p>
            
            {/* Lista de recrutadores atuais */}
            {opportunityRecruiters.length > 0 && (
              <div style={styles.recruitersList}>
                {opportunityRecruiters.map((oppRec, index) => {
                  const recruiter = recruiters.find(r => r.recruiter_id === oppRec.recruiter_id);
                  const isOwner = oppRec.recruiter_id === opportunity.recruiter_id;
                  return (
                    <div key={index} style={styles.recruiterItem}>
                      <div style={styles.recruiterInfo}>
                        <strong>{recruiter?.name || 'Recrutador não encontrado'}</strong>
                        {recruiter?.email && <span> ({recruiter.email})</span>}
                        {isOwner && <span style={styles.ownerBadge}> - DONO DA VAGA</span>}
                        {oppRec.poderoso && <span style={styles.poderosoBadge}> - PODEROSO</span>}
                      </div>
                      {!isOwner && (
                        <button
                          type="button"
                          onClick={() => removeOpportunityRecruiter(index)}
                          style={styles.removeBtn}
                        >
                          Remover
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Formulário para adicionar novo recrutador */}
            <div style={styles.recruiterBlock}>
              <select
                value={newRecruiter.recruiter_id || ''}
                onChange={(e) => updateNewRecruiter('recruiter_id', parseInt(e.target.value))}
                style={{...styles.input, marginBottom: '0.5rem'}}
              >
                <option value="">Selecione um recrutador</option>
                {recruiters
                  .filter(r => 
                    !opportunityRecruiters.some(or => or.recruiter_id === r.recruiter_id) &&
                    r.recruiter_id !== opportunity.recruiter_id
                  )
                  .map(recruiter => (
                    <option key={recruiter.recruiter_id} value={recruiter.recruiter_id}>
                      {recruiter.name} ({recruiter.email})
                    </option>
                  ))
                }
              </select>
              
              <div style={styles.checkboxGrid}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={newRecruiter.poderoso || false}
                    onChange={(e) => updateNewRecruiter('poderoso', e.target.checked)}
                    style={styles.checkbox}
                  />
                  PODEROSO
                </label>
              </div>
              
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                {newRecruiter.recruiter_id && (
                  <button
                    type="button"
                    onClick={clearNewRecruiter}
                    style={styles.removeBtn}
                  >
                    Limpar
                  </button>
                )}
                <button
                  type="button"
                  onClick={addOpportunityRecruiter}
                  style={{...styles.addBtn, marginLeft: 'auto'}}
                  disabled={!newRecruiter.recruiter_id}
                >
                  Adicionar Recrutador
                </button>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Questões para o candidato</h3>
            <p style={styles.sectionDescription}>
              Elabore uma questão que o candidato possa responder em forma de texto direto na candidatura.
            </p>
            
            {questions.filter(q => !q._destroy).map((question, questionIndex) => (
              <div key={questionIndex} style={styles.questionGroup}>
                <div style={styles.questionHeader}>
                  <h4 style={styles.questionTitle}>Pergunta {questionIndex + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeQuestion(questionIndex)}
                    style={styles.removeBtn}
                  >
                    Remover
                  </button>
                </div>
                
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Pergunta *</label>
                  <textarea
                    value={question.body}
                    onChange={(e) => updateQuestion(questionIndex, 'body', e.target.value)}
                    style={styles.textarea}
                    rows={2}
                    placeholder="Elabore uma questão que o candidato possa responder em forma de texto direto na candidatura."
                  />
                </div>
                
                <div style={styles.grid}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Tipo da questão</label>
                    <select
                      value={question.question_type === 'boolean' ? 'multiple_choice' : question.question_type}
                      onChange={(e) => updateQuestion(questionIndex, 'question_type', e.target.value)}
                      style={styles.select}
                    >
                      <option value="text">Texto</option>
                      <option value="multiple_choice">Múltipla escolha</option>
                    </select>
                  </div>
                  
                  <div style={styles.checkboxGrid}>
                    <label style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={question.required}
                        onChange={(e) => updateQuestion(questionIndex, 'required', e.target.checked)}
                        style={styles.checkbox}
                      />
                      Obrigatória
                    </label>
                    <label style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={question.filter}
                        onChange={(e) => updateQuestion(questionIndex, 'filter', e.target.checked)}
                        style={styles.checkbox}
                      />
                      Filtro Habilitado
                    </label>
                  </div>
                </div>
                
                {(question.question_type === 'multiple_choice' || question.question_type === 'boolean') && (
                  <div style={styles.answersSection}>
                    <h5 style={styles.answersTitle}>Respostas:</h5>
                    
                    {question.opportunity_answers.map((answer, answerIndex) => (
                      <div key={answerIndex} style={styles.answerGroup}>
                        <div style={styles.answerRow}>
                          <input
                            type="text"
                            value={answer.body}
                            onChange={(e) => updateAnswer(questionIndex, answerIndex, 'body', e.target.value)}
                            style={styles.input}
                            placeholder="Digite a resposta"
                          />
                          <label style={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={answer.correct}
                              onChange={(e) => updateAnswer(questionIndex, answerIndex, 'correct', e.target.checked)}
                              style={styles.checkbox}
                            />
                            Resposta correta
                          </label>
                          <button
                            type="button"
                            onClick={() => removeAnswer(questionIndex, answerIndex)}
                            style={styles.removeAnswerBtn}
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      onClick={() => addAnswer(questionIndex)}
                      style={styles.addAnswerBtn}
                    >
                      Adicionar resposta correta
                    </button>
                  </div>
                )}
              </div>
            ))}
            
            <button
              type="button"
              onClick={addQuestion}
              style={styles.addBtn}
            >
              Adicionar Novo(a) Opportunity question
            </button>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Publicação</h3>
            <div style={styles.grid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Publicado em</label>
                <input
                  type="datetime-local"
                  value={formatDateTime(opportunity.published_at)}
                  onChange={(e) => handleChange('published_at', e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Expira em</label>
                <input
                  type="datetime-local"
                  value={formatDateTime(opportunity.expires_at)}
                  onChange={(e) => handleChange('expires_at', e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>
            <div style={styles.checkboxGrid}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.published}
                  onChange={(e) => handleChange('published', e.target.checked)}
                  style={styles.checkbox}
                />
                Publicado?
              </label>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>E-mail de resposta customizada para o candidato</h3>
            <div style={styles.checkboxGrid}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.reply_email}
                  onChange={(e) => handleChange('reply_email', e.target.checked)}
                  style={styles.checkbox}
                />
                Ativar e-mail customizado de resposta?
              </label>
            </div>
            {opportunity.reply_email && (
              <>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Reply email subject</label>
                  <input
                    type="text"
                    value={opportunity.reply_email_subject || ''}
                    onChange={(e) => handleChange('reply_email_subject', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Reply email body</label>
                  <textarea
                    value={opportunity.reply_email_body || ''}
                    onChange={(e) => handleChange('reply_email_body', e.target.value)}
                    style={styles.textarea}
                    rows={5}
                  />
                </div>
              </>
            )}
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Template da vaga no frodo</h3>
            <div style={styles.checkboxGrid}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.use_custom_template}
                  onChange={(e) => handleChange('use_custom_template', e.target.checked)}
                  style={styles.checkbox}
                />
                Customizar template
              </label>
            </div>
            {opportunity.use_custom_template && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Template</label>
                <textarea
                  value={opportunity.custom_template || ''}
                  onChange={(e) => handleChange('custom_template', e.target.value)}
                  style={styles.textarea}
                  rows={5}
                />
              </div>
            )}
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Caesar</h3>
            <div style={styles.grid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Caesar Expira em:</label>
                <input
                  type="datetime-local"
                  value={formatDateTime(opportunity.poderoso_expires_at)}
                  onChange={(e) => handleChange('poderoso_expires_at', e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={opportunity.pro}
                    onChange={(e) => handleChange('pro', e.target.checked)}
                    style={styles.checkbox}
                  />
                  Caesar PRO:
                </label>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Moderação</h3>
            <div style={styles.checkboxGrid}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={opportunity.moderated}
                  onChange={(e) => handleChange('moderated', e.target.checked)}
                  style={styles.checkbox}
                />
                Moderado?
              </label>
            </div>
          </div>

          </div>
            <aside style={styles.previewColumn}>
              {renderPreview()}
            </aside>
          </div>

          <div style={styles.actions}>
            <div style={styles.formActions}>
              <button type="button" onClick={onClose} style={styles.cancelBtn}>
                Cancelar
              </button>
              {isModeratingIntegrationOpportunity() && (
                <button
                  type="button"
                  onClick={handleSaveAndActivateIntegration}
                  disabled={isSaving}
                  style={{
                    ...styles.saveBtn,
                    backgroundColor: colors.success,
                    ...(isSaving ? { backgroundColor: colors.textLight, cursor: 'not-allowed' } : {})
                  }}
                >
                  {savingAction === 'save-and-activate' ? 'Salvando e ativando...' : 'Salvar e Ativar'}
                </button>
              )}
              <button 
                type="submit" 
                disabled={isSaving}
                style={{
                  ...styles.saveBtn,
                  ...(isSaving ? { backgroundColor: colors.textLight, cursor: 'not-allowed' } : {})
                }}
              >
                {savingAction === 'save' ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  pageContainer: {
    width: '100%',
  },
  pageShell: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    minHeight: 'calc(100vh - 5rem)',
  },
  pageLoading: {
    width: '100%',
  },
  pageLoadingCard: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '95%',
    maxWidth: '1320px',
    maxHeight: '95vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
    position: 'sticky',
    top: 0,
    backgroundColor: colors.white,
    zIndex: 1,
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
  },
  backBtn: {
    marginBottom: '0.5rem',
    backgroundColor: 'transparent',
    color: colors.primary,
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    fontWeight: 'bold',
  },
  content: {
    padding: '1.5rem',
  },
  editorLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.45fr) minmax(340px, 0.75fr)',
    gap: '1.5rem',
    alignItems: 'start',
  },
  formColumn: {
    minWidth: 0,
  },
  previewColumn: {
    alignSelf: 'start',
  },
  previewCard: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
    minWidth: 0,
    overflow: 'hidden',
  },
  previewHeader: {
    borderBottom: `1px solid ${colors.border}`,
    paddingBottom: '1rem',
    marginBottom: '1rem',
  },
  previewEyebrow: {
    display: 'block',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: '0.75rem',
    color: colors.textLight,
    fontWeight: 'bold',
    marginBottom: '0.5rem',
  },
  previewTitle: {
    margin: '0 0 0.75rem 0',
    color: colors.primary,
    fontSize: '1.45rem',
    lineHeight: 1.2,
  },
  previewBadges: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  previewBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.6rem',
    borderRadius: '999px',
    backgroundColor: colors.primary,
    color: colors.white,
    fontSize: '0.75rem',
    fontWeight: 'bold',
  },
  previewMetaGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
    marginBottom: '1.25rem',
  },
  previewMetaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem',
    padding: '0.75rem',
    backgroundColor: colors.background,
    borderRadius: '8px',
    fontSize: '0.85rem',
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  previewSection: {
    marginBottom: '1.25rem',
  },
  previewSectionTitle: {
    margin: '0 0 0.4rem 0',
    color: colors.primary,
    fontSize: '0.95rem',
    fontWeight: 'bold',
  },
  previewText: {
    color: colors.text,
    fontSize: '0.92rem',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  previewFooter: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: '0.9rem',
  },
  previewLink: {
    color: colors.primary,
    fontWeight: 'bold',
    textDecoration: 'none',
    marginTop: '0.4rem',
  },
  section: {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: colors.background,
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
  },
  sectionTitle: {
    margin: '0 0 1.5rem 0',
    color: colors.primary,
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },
  sectionDescription: {
    margin: '0 0 1.5rem 0',
    color: colors.textLight,
    fontSize: '0.9rem',
    lineHeight: '1.4',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1rem',
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
  },
  inputGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
    color: colors.text,
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: colors.text,
    cursor: 'pointer',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
    backgroundColor: colors.white,
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  inputError: {
    borderColor: colors.error,
    boxShadow: `0 0 0 2px ${colors.error}22`,
    backgroundColor: '#FFF7F7',
  },
  fieldError: {
    marginTop: '0.35rem',
    color: colors.error,
    fontSize: '0.82rem',
    fontWeight: 'bold',
  },
  checkbox: {
    width: '1.2rem',
    height: '1.2rem',
  },
  questionGroup: {
    marginBottom: '2rem',
    padding: '1.5rem',
    border: `2px solid ${colors.border}`,
    borderRadius: '8px',
    backgroundColor: colors.white,
  },
  questionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  questionTitle: {
    margin: 0,
    color: colors.primary,
    fontSize: '1.1rem',
    fontWeight: 'bold',
  },
  answersSection: {
    marginTop: '1.5rem',
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
    border: `1px solid ${colors.border}`,
  },
  answersTitle: {
    margin: '0 0 1rem 0',
    color: colors.text,
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  answerGroup: {
    marginBottom: '1rem',
  },
  answerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  addAnswerBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontFamily: 'inherit',
  },
  removeAnswerBtn: {
    padding: '0.5rem 0.75rem',
    backgroundColor: colors.error,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  removeBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.error,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontFamily: 'inherit',
  },
  addBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    marginTop: '1rem',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '2rem',
    marginTop: '2rem',
    padding: '1.5rem',
    borderTop: `1px solid ${colors.border}`,
    backgroundColor: colors.background,
    position: 'sticky',
    bottom: 0,
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
  },
  actionCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '1.5rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    backgroundColor: colors.white,
    textAlign: 'center',
  },
  actionEmoji: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  actionButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    fontWeight: 'bold',
    marginBottom: '1rem',
    minWidth: '150px',
  },
  actionDescription: {
    margin: 0,
    fontSize: '0.9rem',
    color: colors.textLight,
    lineHeight: '1.4',
  },
  statusCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '2rem',
    border: `2px solid ${colors.border}`,
    borderRadius: '8px',
    backgroundColor: colors.background,
    textAlign: 'center',
  },
  statusEmoji: {
    fontSize: '4rem',
    marginBottom: '1rem',
  },
  statusTitle: {
    margin: '0 0 1rem 0',
    color: colors.primary,
    fontSize: '1.3rem',
    fontWeight: 'bold',
  },
  statusInfo: {
    marginBottom: '1rem',
    textAlign: 'left',
    width: '100%',
  },
  statusDescription: {
    margin: 0,
    fontSize: '0.9rem',
    color: colors.textLight,
    lineHeight: '1.4',
    fontStyle: 'italic',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
  cancelBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.white,
    color: colors.textLight,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
  },
  saveBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    fontWeight: 'bold',
  },
  loading: {
    textAlign: 'center',
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  urlRow: {
    marginBottom: '1.5rem',
  },
  urlLink: {
    color: colors.primary,
    textDecoration: 'none',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    backgroundColor: colors.background,
    display: 'block',
    flex: 1,
    fontSize: '1rem',
    wordBreak: 'break-all',
  },
  urlContainer: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'stretch',
  },
  copyBtn: {
    padding: '0.75rem 1rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  textDisplay: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    backgroundColor: colors.background,
    fontSize: '1rem',
    fontFamily: 'inherit',
    color: colors.text,
    userSelect: 'text',
    cursor: 'text',
  },
  recruitersList: {
    marginBottom: '1rem',
  },
  recruiterItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    marginBottom: '0.5rem',
    backgroundColor: colors.white,
  },
  recruiterInfo: {
    flex: 1,
    fontSize: '0.9rem',
    lineHeight: '1.4',
  },
  recruiterBlock: {
    padding: '1rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    marginBottom: '1rem',
    backgroundColor: colors.background,
  },
  ownerBadge: {
    color: colors.primary,
    fontWeight: 'bold',
  },
  poderosoBadge: {
    color: '#FFD700',
    fontWeight: 'bold',
  },
};

export default OpportunityEditModal;
